/**
 * compress.js — Layer 1 client-side compression
 *
 * Uses browser-image-compression to shrink 40–60 MB modern camera JPEGs/HEICs
 * down to ~500–800 kB WebP before they ever touch the network. Runs entirely
 * in a Web Worker so the UI stays responsive on low-end phones.
 *
 * Two entry points:
 *   compressImage(file)   → File (WebP, ≤ ~1 MB, longest side ≤ 1920 px)
 *   validateVideo(file)   → { ok, error }  (no compression — enforce a hard cap)
 */
import imageCompression from "browser-image-compression";

const IMAGE_TARGET = {
  maxSizeMB: 1.0,          // client cap; server allows up to 15
  maxWidthOrHeight: 1920,  // 2K-ready without wasting bytes
  useWebWorker: true,
  initialQuality: 0.82,
  fileType: "image/webp",  // 25–40 % smaller than jpeg at same quality
};

const AVATAR_TARGET = {
  maxSizeMB: 0.3,
  maxWidthOrHeight: 512,
  useWebWorker: true,
  initialQuality: 0.85,
  fileType: "image/webp",
};

const VIDEO_HARD_LIMIT_MB = 60;
const IMAGE_ORIGINAL_SANE_LIMIT_MB = 25;  // reject "screen recordings pasted as image" pathologies

/**
 * Compress a single image file. HEIC/HEIF from iPhones is decoded by browser‑image‑compression
 * automatically when possible; if not, we fall back to the original file and let the server reject.
 * @param {File} file
 * @param {"listing"|"avatar"} preset
 * @returns {Promise<File>}
 */
export async function compressImage(file, preset = "listing") {
  if (!file || !file.type?.startsWith("image/")) return file;

  // Sanity gate — if the raw file is unreasonably huge, still try; browser-image-compression
  // will stream through canvas and produce a saner output.
  if (file.size > IMAGE_ORIGINAL_SANE_LIMIT_MB * 1024 * 1024) {
    console.warn(`[compress] raw file ${(file.size / 1024 / 1024).toFixed(1)} MB — attempting compression`);
  }

  const options = preset === "avatar" ? AVATAR_TARGET : IMAGE_TARGET;
  try {
    const t0 = performance.now();
    const compressed = await imageCompression(file, options);
    const savings = 1 - compressed.size / Math.max(1, file.size);
    console.info(
      `[compress] ${file.name}: ${(file.size / 1024).toFixed(0)} kB → ` +
      `${(compressed.size / 1024).toFixed(0)} kB (` +
      `${(savings * 100).toFixed(0)}% saved, ${(performance.now() - t0).toFixed(0)} ms)`
    );
    // Preserve a meaningful filename (browser-image-compression sometimes drops it)
    const outName = compressed.name || file.name.replace(/\.(jpg|jpeg|png|heic|heif)$/i, ".webp");
    return new File([compressed], outName, { type: compressed.type || "image/webp" });
  } catch (err) {
    console.warn(`[compress] failed for ${file.name}, using original:`, err);
    return file;   // graceful degradation — server has its own cap
  }
}

export function validateVideo(file, maxMb = VIDEO_HARD_LIMIT_MB) {
  if (!file) return { ok: false, error: "No file" };
  if (!file.type?.startsWith("video/")) return { ok: false, error: "Not a video" };
  const sizeMb = file.size / (1024 * 1024);
  if (sizeMb > maxMb) {
    return { ok: false, error: `Video is ${sizeMb.toFixed(1)} MB. Max is ${maxMb} MB — please trim it before uploading.` };
  }
  return { ok: true };
}

/**
 * Compress a list of files in parallel with a small concurrency cap so low-end
 * phones don't hit the JS heap limit. Non-image files pass through untouched.
 */
export async function compressBatch(files, preset = "listing", concurrency = 2) {
  const arr = Array.from(files || []);
  const out = new Array(arr.length);
  let i = 0;
  await Promise.all(
    Array.from({ length: Math.min(concurrency, arr.length) }, async () => {
      while (i < arr.length) {
        const idx = i++;
        const f = arr[idx];
        out[idx] = f.type?.startsWith("image/") ? await compressImage(f, preset) : f;
      }
    })
  );
  return out;
}

export const _internals = { IMAGE_TARGET, AVATAR_TARGET, VIDEO_HARD_LIMIT_MB };
